import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { qk } from '@/lib/api/keys';
import type { Job } from '@/lib/api/types';
import { parseEvent } from './events';

/**
 * UNE seule connexion SSE pour toute l'application.
 *
 * HTTP/1.1 plafonne à 6 connexions par origine : un EventSource par carte de
 * run figerait l'interface dès la sixième, plus aucune requête ne partirait.
 * Les événements sont écrits directement dans le cache TanStack Query, ce qui
 * rend le repli en polling gratuit — les écrans lisent le même cache.
 */
const LiveContext = createContext<boolean>(false);

export function useIsLive(): boolean {
  return useContext(LiveContext);
}

export function EventStreamProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [live, setLive] = useState(false);
  const retryRef = useRef(0);

  useEffect(() => {
    let source: EventSource | null = null;
    let timer: number | undefined;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      source = new EventSource('/api/events');

      source.onopen = () => {
        setLive(true);
        retryRef.current = 0;
      };

      source.onmessage = (message) => {
        const event = parseEvent(message.data as string);
        if (!event) return;

        // Écriture directe dans le cache : l'écran de détail et la liste
        // lisent la même donnée, sans requête supplémentaire.
        client.setQueryData(qk.job(event.id), (previous: Job | undefined) =>
          previous ? { ...previous, ...event } : previous);
        client.setQueryData(qk.jobs(), (previous: Job[] | undefined) =>
          previous?.map((job) => (job.id === event.id ? { ...job, ...event } : job)));

        if (event.type === 'run.finished') {
          void client.invalidateQueries({ queryKey: qk.jobs() });
          void client.invalidateQueries({ queryKey: qk.quota() });
        }
      };

      source.onerror = () => {
        setLive(false);
        source?.close();
        // Backoff exponentiel plafonné : une coupure réseau ne doit pas
        // provoquer une tempête de reconnexions.
        const delay = Math.min(30_000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        timer = window.setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      source?.close();
    };
  }, [client]);

  return <LiveContext.Provider value={live}>{children}</LiveContext.Provider>;
}
